export interface ProgressData {
  progress: number; // 0-99
  lastUpdate: string; // ISO timestamp
  startTime: string; // ISO timestamp
}

// Configuration constants
export const PROGRESS_CONFIG = {
  TOTAL_DAYS: 5,
  TOTAL_MINUTES: 5 * 24 * 60, // 7200 minutes
  MAX_PROGRESS: 99,
  UPDATE_INTERVAL: 60 * 1000, // 1 minute in milliseconds
} as const;

export const PROGRESS_RATE = PROGRESS_CONFIG.MAX_PROGRESS / PROGRESS_CONFIG.TOTAL_MINUTES; // ~0.01375% per minute

// Storage keys
export const STORAGE_KEYS = {
  PROGRESS: "chatPlaceloader_progress",
  LAST_UPDATE: "chatPlaceloader_lastUpdate",
  START_TIME: "chatPlaceloader_startTime",
} as const;

// Storage utility functions
export const getProgressData = (): ProgressData | null => {
  try {
    const progress = localStorage.getItem(STORAGE_KEYS.PROGRESS);
    const lastUpdate = localStorage.getItem(STORAGE_KEYS.LAST_UPDATE);
    const startTime = localStorage.getItem(STORAGE_KEYS.START_TIME);

    if (!progress || !lastUpdate || !startTime) {
      return null;
    }

    const progressNum = parseFloat(progress);
    if (isNaN(progressNum) || progressNum < 0 || progressNum > PROGRESS_CONFIG.MAX_PROGRESS) {
      return null;
    }

    return {
      progress: progressNum,
      lastUpdate,
      startTime,
    };
  } catch (error) {
    console.warn("Error reading progress data from localStorage:", error);
    return null;
  }
};

export const saveProgressData = (data: ProgressData): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.PROGRESS, data.progress.toString());
    localStorage.setItem(STORAGE_KEYS.LAST_UPDATE, data.lastUpdate);
    localStorage.setItem(STORAGE_KEYS.START_TIME, data.startTime);
  } catch (error) {
    console.warn("Error saving progress data to localStorage:", error);
  }
};

export const clearProgressData = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.PROGRESS);
    localStorage.removeItem(STORAGE_KEYS.LAST_UPDATE);
    localStorage.removeItem(STORAGE_KEYS.START_TIME);
  } catch (error) {
    console.warn("Error clearing progress data from localStorage:", error);
  }
};

// Progress calculation functions
export const calculateMinutesElapsed = (fromTime: string, toTime: string): number => {
  const from = new Date(fromTime).getTime();
  const to = new Date(toTime).getTime();
  return Math.max(0, Math.floor((to - from) / (1000 * 60)));
};

export const calculateBackgroundProgress = (data: ProgressData): number => {
  const now = new Date().toISOString();
  const minutesElapsed = calculateMinutesElapsed(data.lastUpdate, now);

  if (minutesElapsed === 0) {
    return data.progress;
  }

  // Calculate new progress based on time elapsed
  const progressIncrease = minutesElapsed * PROGRESS_RATE;
  const newProgress = data.progress + progressIncrease;

  // Never exceed MAX_PROGRESS (99%)
  return Math.min(newProgress, PROGRESS_CONFIG.MAX_PROGRESS);
};

export const initializeProgressData = (): ProgressData => {
  const now = new Date().toISOString();
  return {
    progress: 0,
    lastUpdate: now,
    startTime: now,
  };
};

// Helper function to get human-readable time remaining
export const getTimeRemaining = (data: ProgressData): string => {
  const totalMinutesElapsed = calculateMinutesElapsed(data.startTime, new Date().toISOString());
  const minutesRemaining = Math.max(0, PROGRESS_CONFIG.TOTAL_MINUTES - totalMinutesElapsed);

  const days = Math.floor(minutesRemaining / (24 * 60));
  const hours = Math.floor((minutesRemaining % (24 * 60)) / 60);
  const minutes = minutesRemaining % 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
};

// Check if the full 5-day period has elapsed
export const isProcessingComplete = (data: ProgressData): boolean => {
  const totalMinutesElapsed = calculateMinutesElapsed(data.startTime, new Date().toISOString());
  return totalMinutesElapsed >= PROGRESS_CONFIG.TOTAL_MINUTES;
};
